// Autobahn (auto.de redesign) — NEW HOME (/new-main) page data & config.
//
// STAGE 3: no fake inventory left in this file. What remains is either
//   - marketing COPY (hero campaign slides + promos until the WP admin lands,
//     testimonials pending a real review source, guarantee/steps texts),
//   - or CONFIG describing real searches (curated filter sets, body-type tiles,
//     SEO directory deep-links, hero dropdown option values).
// Blocks whose data source is down/absent now HIDE instead of rendering demo
// content — see the block notes in NewMainPage.tsx.
// ---------------------------------------------------------------------------
// studio-style car placeholder (white backdrop + floor shadow + silhouette),
// tinted per car so the static cards don't look cloned. Data-URI SVG — no
// asset pipeline, no external requests.
// ---------------------------------------------------------------------------
const carSvg = (body) => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 600">` +
        `<rect width="800" height="600" fill="#ffffff"/>` +
        `<ellipse cx="400" cy="474" rx="290" ry="24" fill="rgba(16,22,42,.12)"/>` +
        `<path d="M160 434 q0-70 62-84 l58-13 q62-53 152-53 h58 q92 0 152 60 l58 10 q62 12 62 80 v14 q0 20-20 20 h-562 q-20 0-20-20 z" fill="${body}"/>` +
        `<path d="M302 342 q48-44 128-44 h48 q68 0 118 48 z" fill="rgba(255,255,255,.4)"/>` +
        `<circle cx="272" cy="452" r="44" fill="#2b3038"/><circle cx="272" cy="452" r="18" fill="#9aa3ad"/>` +
        `<circle cx="560" cy="452" r="44" fill="#2b3038"/><circle cx="560" cy="452" r="18" fill="#9aa3ad"/>` +
        `</svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};
const CAR_RED = carSvg('#C4523E');
// ---------------------------------------------------------------------------
// hero search — dropdown option values (rate €/mtl., mileage km). Both fields are searchable
// comboboxes (Dropdown's allowCustomValue) — these rungs are just the suggested/scrollable
// picks, typing any other amount is still accepted, see NewMainPage's HeroSection. No fallback
// claim numbers: when the live counts are not (yet) available the copy renders its number-less
// variant instead of inventing inventory figures.
// ---------------------------------------------------------------------------
const range = (from, to, step) => {
    const out = [];
    for (let v = from; v <= to; v += step)
        out.push(v);
    return out;
};
export const HERO_RATES = range(50, 500, 50);
export const HERO_KM = range(0, 150000, 5000);
/** the permanent first slide — brand promise texts, not inventory data. */
export const BRAND_SLIDE = {
    kind: 'brand',
    stats: [['12 Monate', 'Garantie inklusive'], ['14 Tage', 'Rückgaberecht'], ['0 €', 'Anzahlung möglich']],
};
export const PROMO_BG = {
    blue: 'linear-gradient(120deg,#0B53CC 0%,#0A3A86 60%,#072449 100%)',
    cyan: 'linear-gradient(120deg,#0E7490 0%,#0B5566 60%,#06323D 100%)',
    teal: 'linear-gradient(120deg,#075A75 0%,#064B5F 55%,#04323F 100%)',
};
// ---------------------------------------------------------------------------
// body types (block 6) — [car-schema selector key, lucide icon, German
// fallback label, autobahn bodyType translation key]. The selector key is
// BOTH the BODY_TYPE filter value the SRP expects in the URL AND the key of
// its aggregation bucket in the search response. NewMainPage's BodyTypeRow
// translates via t(`autobahn:bodyType.<i18nKey>`) — the SAME short label set
// (@bytebrand/fe-ui-core-autobahn locales/{de,en}/autobahn.json `bodyType`),
// already globally loaded, so the tile re-labels on a language switch without
// falling back to the cbd filter-dropdown dictionary's longer names
// ('SUV/Geländewagen', 'Van/Kleinbus'). The German label here is only a
// fallback for if that key is ever missing. Curated to the 6 mainstream
// categories the autobahn dictionary actually has — order is fixed display
// order, NOT sorted by live count.
// ---------------------------------------------------------------------------
export const BODY_TYPE_TILES = [
    ['selector_category_compact', 'car', 'Kleinwagen', 'smallCar'],
    ['selector_category_sedan', 'car-front', 'Limousine', 'sedan'],
    ['selector_category_combi', 'caravan', 'Kombi', 'estate'],
    ['selector_category_suv', 'mountain', 'SUV', 'suv'],
    ['selector_category_convertible', 'sun', 'Cabrio', 'convertible'],
    ['selector_category_van', 'box', 'Van', 'van'],
];
const EL_HYBRID_FUELS = [
    'selector_fuel_electrical',
    'selector_fuel_hybridElectricalPetrol',
    'selector_fuel_hybridElectricalDiesel',
];
// 2026-07-28 redesign (Figma "auto.de | Website 2026", Beliebte Suchen): two of the four tiles'
// specced criteria have NO matching SRP filter today and are intentionally left out rather than
// invented — "Vehicle type: Passenger car" (BODY_TYPE is body SHAPE, e.g. SUV/Kombi; car-schema's
// vehicleClass field for PKW-vs-motorcycle/commercial exists but isn't wired into FilterFactory or
// KNOWN_FILTER_KEYS) and "Damaged vehicles: Exclude" (no accident/damage-condition filter exists on
// the SRP at all — VEHICLE_CONDITION is new-vs-used only). Neither ever rendered as its own chip in
// the mockup either (chips only ever show 3-4 fields), so this doesn't change what the tile visibly
// promises — only the underlying query is a little broader than the spec until those filters exist.
export const CURATED_SEARCHES = [
    {
        icon: 'tag', title: 'Gute Gebrauchte bis 20.000 €',
        chips: ['bis 20.000 €', 'ab 2018', 'bis 100.000 km'],
        query: 'PRICE[to]=20000&FIRST_REGISTRATION[from]=2018&MILEAGE[to]=100000',
        body: { filters: {
                absolutValues: {},
                noAbsolutValues: {},
                rangeValues: {
                    price_currentSalesPrice: { to: 20000 },
                    mainData_firstRegistrationYear: { from: 2018 },
                    mainData_mileage: { to: 100000 },
                },
            } },
    },
    {
        icon: 'users', title: 'SUV & Familienautos',
        chips: ['Kombi & SUV', 'Sitzplätze ab 5', 'ab 2018', 'bis 100.000 km'],
        query: 'BODY_TYPE[0]=selector_category_combi&BODY_TYPE[1]=selector_category_suv&BODY_TYPE[2]=selector_category_van'
            + '&SEATS[from]=5&FIRST_REGISTRATION[from]=2018&MILEAGE[to]=100000',
        body: { filters: {
                absolutValues: {},
                noAbsolutValues: { BODY_TYPE: ['selector_category_combi', 'selector_category_suv', 'selector_category_van'] },
                rangeValues: {
                    mainData_seats: { from: 5 },
                    mainData_firstRegistrationYear: { from: 2018 },
                    mainData_mileage: { to: 100000 },
                },
            } },
    },
    {
        icon: 'battery-charging', title: 'Elektro & Hybrid',
        chips: ['Elektro / Hybrid', 'ab 2021', 'bis 400 €/mtl.'],
        query: EL_HYBRID_FUELS.map((f, i) => `FUEL[${i}]=${f}`).join('&')
            + '&FIRST_REGISTRATION[from]=2021'
            + '&RATE[monthlyInstallmentFrom]=&RATE[monthlyInstallmentTo]=400&RATE[firstInstallment]=0&RATE[paybackPeriod]=60',
        body: { filters: {
                absolutValues: {},
                noAbsolutValues: { FUEL: EL_HYBRID_FUELS },
                rangeValues: { mainData_firstRegistrationYear: { from: 2021 } },
                ignoredValues: { RATE: { monthlyInstallmentFrom: '', monthlyInstallmentTo: 400, firstInstallment: 0, paybackPeriod: 60 } },
            } },
    },
    {
        icon: 'gauge', title: 'Automatik & Komfort',
        chips: ['ab 2019', 'bis 100.000 km', '360° Kamera'],
        query: 'TRANSMISSION[0]=selector_gearbox_automatic&FIRST_REGISTRATION[from]=2019&MILEAGE[to]=100000'
            + '&PARKING_ASSISTANTS[0]=selector_parkingAssistants_360Camera',
        body: { filters: {
                absolutValues: {},
                // PARKING_ASSISTANTS is one of the isValueMustBeArray equipment filters — transformFiltersToQuery
                // splits each selected value into its own `${key}_${value}` noAbsolutValues entry, NOT a plain
                // noAbsolutValues.PARKING_ASSISTANTS array (see FiltersUtils.ts transformFiltersToQuery).
                noAbsolutValues: {
                    TRANSMISSION: ['selector_gearbox_automatic'],
                    PARKING_ASSISTANTS_selector_parkingAssistants_360Camera: ['selector_parkingAssistants_360Camera'],
                },
                rangeValues: { mainData_firstRegistrationYear: { from: 2019 }, mainData_mileage: { to: 100000 } },
            } },
    },
];
// brand wall (block 10) — REAL brand marks from the legacy theme's asset set
// (themes/autode/front/img/top-brands, copied to media/images/brands). The maker
// LIST itself is live (information/makers top-tier + per-make counts); makers
// without a local logo render a neutral monogram circle.
/* eslint-disable global-require */
export const BRAND_LOGO_SRC = {
    volkswagen: require('media/images/brands/vw.png'),
    'mercedes-benz': require('media/images/brands/mers.png'),
    audi: require('media/images/brands/audi.png'),
    opel: require('media/images/brands/opel.png'),
    bmw: require('media/images/brands/bmw.png'),
    ford: require('media/images/brands/ford.png'),
    'škoda': require('media/images/brands/skoda.png'),
    skoda: require('media/images/brands/skoda.png'),
    hyundai: require('media/images/brands/hyundai.png'),
    kia: require('media/images/brands/kia.png'),
    mazda: require('media/images/brands/mazda.png'),
    dacia: require('media/images/brands/dacia.png'),
    seat: require('media/images/brands/seat.png'),
};
/* eslint-enable global-require */
// (block 12 Leasing-Angebote is fully live now: real leasing-active cars picked
// from the recommendation/search pools — see leasingRows in NewMainPage.tsx.)
export const TESTIMONIALS = [
    ['Sandra M.', 'Audi A4 Avant', 'Komplett online gekauft, vier Tage später stand der Wagen vor der Tür. Genau wie beschrieben — kein Haken.', 5],
    ['Thomas K.', 'BMW 320d Touring', 'Die Preisbewertung hat mir die Verhandlung abgenommen. Faire Rate, transparente Konditionen.', 5],
    ['Yusuf D.', 'VW Golf', 'Skeptisch beim Online-Kauf — aber die 14-tägige Rückgabe gab mir Sicherheit. Top.', 4],
];
export const MAGAZINE = [
    { cat: 'Kaufberatung', title: 'Gebrauchtwagen-Check: die 10 wichtigsten Punkte', read: '6 Min.',
        icon: 'clipboard-check', grad: 'linear-gradient(135deg,#0B57D0,#0A2F86)' },
    { cat: 'Elektromobilität', title: 'E-Auto-Förderung 2026 — das ändert sich für Käufer', read: '4 Min.',
        icon: 'battery-charging', grad: 'linear-gradient(135deg,#0E7490,#06323D)' },
    { cat: 'Ratgeber', title: 'Leasing oder Finanzierung? Der ehrliche Vergleich', read: '5 Min.',
        icon: 'bar-chart-3', grad: 'linear-gradient(135deg,#075A75,#04323F)' },
];
// ---------------------------------------------------------------------------
// SEO directory (bottom) — editorial link lists with REAL deep-link targets.
//   SEO_MAKES/SEO_MODELS/SEO_BODY: hrefs are built in the page as
//   /angebot/<slug>/ deep links — a SEPARATE Next.js micro-frontend (own
//   routing, confirmed live with the same slugs on both auto.de and
//   autode-dev.de), rendered as a real crawlable <a href> + a FULL page
//   navigation (NOT an SPA history.push — see NewMainPage's angebotSlug /
//   SeoDirectory onClick).
//   SEO_MODELS: [display label, make (ES mainData_make), model (ES mainData_model)]
//   SEO_BODY:   [label, SRP query string] — the query string is currently
//   unused (kept for the /search fallback path) now that Karosserie links to
//   the real /angebot/<body-slug>/ page.
//   SEO_FUELS:  [label, href] — the 'Kraftstoffart' column. Some fuel types
//   have a real /angebot/ landing page, others don't yet, so unlike the other
//   three columns this one stores the FULL href directly (mixed /angebot/ and
//   /search?FUEL[...] targets — confirmed per-entry, not guessed).
// ---------------------------------------------------------------------------
export const SEO_MAKES = ['Volkswagen', 'BMW', 'Audi', 'Mercedes-Benz', 'Opel', 'Škoda', 'Ford', 'Renault'];
export const SEO_MODELS = [
    ['VW Golf', 'Volkswagen', 'Golf'],
    ['Opel Corsa', 'Opel', 'Corsa'],
    ['Audi A4', 'Audi', 'A4'],
    ['VW Tiguan', 'Volkswagen', 'Tiguan'],
    ['Audi Q5', 'Audi', 'Q5'],
    ['BMW X1', 'BMW', 'X1'],
    ['Ford Focus', 'Ford', 'Focus'],
    ['Škoda Octavia', 'Škoda', 'Octavia'],
];
export const SEO_BODY = [
    ['SUV', 'BODY_TYPE[0]=selector_category_suv'],
    ['Kombi', 'BODY_TYPE[0]=selector_category_combi'],
    ['Limousine', 'BODY_TYPE[0]=selector_category_sedan'],
    ['Kleinwagen', 'BODY_TYPE[0]=selector_category_compact'],
    ['Cabrio', 'BODY_TYPE[0]=selector_category_convertible'],
    ['Van', 'BODY_TYPE[0]=selector_category_van'],
    ['Coupé', 'BODY_TYPE[0]=selector_category_coupe'],
    ['Transporter', 'BODY_TYPE[0]=selector_category_transporter'],
];
export const SEO_FUELS = [
    ['Benzin', '/angebot/gebrauchtwagen/benzin/'],
    ['Diesel', '/angebot/gebrauchtwagen/diesel/'],
    ['Elektro', '/angebot/elektroauto/'],
    ['Hybrid (Benzin)', '/angebot/hybrid/'],
    ['Hybrid (Diesel)', '/search?FUEL[0]=selector_fuel_hybridElectricalDiesel'],
    ['Erdgas', '/search?FUEL[0]=selector_fuel_naturalGas'],
    ['Wasserstoff', '/angebot/wasserstoffauto/'],
    ['Ethanol', '/search?FUEL[0]=selector_fuel_ethanol'],
];
export const SELL_CAR_IMG = CAR_RED;
